import { clamp } from "lib";
import { DOM_NODE_TYPES } from "lib/textUtils";

/**
 * @desc gets the current window selection and returns the startOffset and endOffset of the current selection
 * the ancestor property can be passed to also get the index of which child the range is in from the target ancestor (if it is a child of the ancestor)
 */
export const getCurrentRangeValues = ancestor => {
  // get current selection range
  const selection = window.getSelection();

  let range = {
    startOffset: 0,
    endOffset: 0
  };
  if (selection && selection.rangeCount > 0) {
    range = selection.getRangeAt(0);
  }

  let rangeTargetIndex = 0;

  if (ancestor) {
    const ancestorChildNodes = Array.from(ancestor.childNodes);
    rangeTargetIndex = Math.max(
      ancestorChildNodes.indexOf(range.endContainer),
      0
    );
  }

  const rangeStart = range.startOffset;
  const rangeEnd = range.endOffset;

  return { rangeStart, rangeEnd, rangeTargetIndex };
};

export const setRangeForTarget = ({ target, startOffset, endOffset }) => {
  let _target = target;

  // for element nodes (SPAN, DIV, etc.), navigate to their text component
  if (_target && _target.nodeType === Node.ELEMENT_NODE) {
    _target = _target.firstChild;
  }

  // guard against null/undefined target (e.g., empty element with no firstChild)
  if (!_target) {
    return;
  }

  // get the correct max length based on node type:
  // - for CharacterData nodes (text, comment): use the length property
  // - for Element nodes: Range API uses childNodes.length as max offset
  const targetLength =
    _target.nodeType === Node.TEXT_NODE
      ? _target.length
      : _target.childNodes?.length ?? 0;

  // clamp the offsets inside the target
  const _startOffset = clamp(startOffset, 0, targetLength);
  const _endOffset = clamp(endOffset, 0, targetLength);

  // build new range
  const newRange = new Range();

  newRange.setStart(_target, _startOffset);
  newRange.setEnd(_target, _endOffset);

  // apply the new range
  const selection = document.getSelection();
  selection.removeAllRanges();
  selection.addRange(newRange);
};

export const clearSelection = ({ domElement }) => {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (domElement && !domElement.contains(selection.baseNode)) {
      // when a domElement is passed in we want to make sure the target is inside it
      return;
    } else if (selection.empty) {
      // Chrome
      selection.empty();
    } else if (selection.removeAllRanges) {
      // Firefox
      selection.removeAllRanges();
    }
  } else if (document.selection) {
    // IE?
    document.selection.empty();
  }
};

export const moveCursorToEndOfContent = ({ target }) => {
  let _target = target;

  if (target.nodeName === DOM_NODE_TYPES.span) {
    // for spans we need to target their text component
    _target = target.firstChild;
  }

  const length = _target.textContent.length;

  setRangeForTarget({
    target: _target,
    startOffset: length,
    endOffset: length
  });
};

export function selectHtmlNodeText(node) {
  if (window.getSelection && document.createRange) {
    const range = document.createRange();
    range.selectNodeContents(node);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } else if (document.body.createTextRange) {
    const range = document.body.createTextRange();
    range.moveToElementText(node);
    range.select();
  }
}
